import type { ReactNode } from "react";
import type { WorkTypeCode } from "../api/types";

/**
 * Work type pictograms (GRF-10). Original 16 x 16 line drawings, painted with `currentColor` so the same shape works
 * white-on-colour (delivery types) and ink-on-white (RAID register types). Each type has its own SHAPE as well as its
 * own tile colour, so the type stays readable without colour; the text label always travels with it (WorkTypeTile).
 */
const S = { fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" } as const;

const SHAPES: Record<WorkTypeCode, ReactNode> = {
  // Open book: a story is something told.
  STORY: (
    <>
      <path {...S} d="M8 4.5C6.6 3.6 4.9 3.3 3 3.5v8.4c1.9-.2 3.6.1 5 1 1.4-.9 3.1-1.2 5-1V3.5c-1.9-.2-3.6.1-5 1Z" />
      <path {...S} d="M8 4.5v8.4" />
    </>
  ),
  // Tick: a task is done or not done.
  TASK: <path {...S} strokeWidth={2} d="M3.6 8.4 6.6 11.2 12.4 4.8" />,
  // Small square hanging off a branch: a piece of a parent item.
  SUBTASK: (
    <>
      <path {...S} d="M4 3v6.2c0 .9.7 1.6 1.6 1.6H8" />
      <rect x="8.4" y="8.4" width="4.8" height="4.8" rx="1" fill="currentColor" />
    </>
  ),
  // Beetle: body, head and legs.
  DEFECT: (
    <>
      <ellipse cx="8" cy="9.4" rx="3" ry="3.6" fill="currentColor" />
      <circle cx="8" cy="4.6" r="1.6" fill="currentColor" />
      <path {...S} strokeWidth={1.3} d="M3 7.4h2M3 10.6h2M11 7.4h2M11 10.6h2M3.6 13.4l1.6-1.2M12.4 13.4l-1.6-1.2" />
    </>
  ),
  // Lightning bolt: a large body of work.
  EPIC: <path d="M9.2 2 3.8 9.1h3.6L6.6 14l5.6-7.3H8.6L9.2 2Z" fill="currentColor" />,
  // Two opposed arrows: a change.
  CR: <path {...S} d="M3 5.5h8.5M9.3 3.3l2.2 2.2-2.2 2.2M13 10.5H4.5M6.7 8.3l-2.2 2.2 2.2 2.2" />,
  // Speech bubble: someone is asking for something.
  SERVICE_REQUEST: (
    <>
      <path {...S} d="M3 4.6c0-.9.7-1.6 1.6-1.6h6.8c.9 0 1.6.7 1.6 1.6v4.3c0 .9-.7 1.6-1.6 1.6H7.2L4.6 12.8v-2.3c-.9 0-1.6-.7-1.6-1.6V4.6Z" />
      <path {...S} strokeWidth={1.3} d="M5.8 6.8h4.4" />
    </>
  ),
  // Flame: something is on fire now.
  INCIDENT: (
    <path
      d="M8.3 1.8c.3 2-1 2.9-2.1 4.1C5 7.2 4.2 8.4 4.2 10c0 2.3 1.7 4 3.8 4s3.8-1.7 3.8-4c0-1.6-.8-2.6-1.4-3.4-.1 1-.6 1.7-1.3 2 .4-2.4-.1-4.9-.8-6.8Z"
      fill="currentColor"
    />
  ),
  // Up arrow on a base line: shipping to an environment.
  DEPLOYMENT: <path {...S} d="M8 11V3.4M4.8 6.4 8 3.2l3.2 3.2M3.4 13h9.2" />,
  // RAID register types (white tile, ink border).
  // Checklist: the legacy combined issue-or-action type.
  ISSUE_ACTION: <path {...S} strokeWidth={1.4} d="M3 4.4 4 5.4l1.8-1.9M3 10.4l1 1 1.8-1.9M8 4.6h5M8 10.6h5" />,
  // Flag: an issue raised.
  ISSUE: <path {...S} strokeWidth={1.4} d="M4.2 14V2.6M4.2 3h7.4l-1.6 2.6 1.6 2.6H4.2" />,
  // Arrow into a target line: an action to carry out.
  ACTION: <path {...S} strokeWidth={1.4} d="M2.8 8h7.4M7.6 5l3 3-3 3M13 3.6v8.8" />,
  // Warning triangle: a risk that may happen.
  RISK: (
    <>
      <path {...S} strokeWidth={1.4} d="M8 2.6 14 13H2L8 2.6Z" />
      <path {...S} strokeWidth={1.6} d="M8 6.6v3" />
      <circle cx="8" cy="11.4" r=".9" fill="currentColor" />
    </>
  ),
};

/** Question mark in a circle: a type this build does not know yet (a newer server, a bad fixture). */
const UNKNOWN: ReactNode = (
  <>
    <circle {...S} strokeWidth={1.4} cx="8" cy="8" r="5.6" />
    <path {...S} strokeWidth={1.4} d="M6.4 6.4c0-1 .7-1.6 1.6-1.6s1.6.6 1.6 1.5c0 1.2-1.6 1.3-1.6 2.5" />
    <circle cx="8" cy="11.1" r=".8" fill="currentColor" />
  </>
);

function hasWorkTypeIcon(type: string): type is WorkTypeCode {
  return Object.prototype.hasOwnProperty.call(SHAPES, type);
}

/** The bare pictogram, decorative: the caller always provides the accessible name next to it. */
export function WorkTypeIcon({ type, size = 12 }: { type: string; size?: number }) {
  return (
    <svg
      className="gf-type-icon"
      data-type-icon={hasWorkTypeIcon(type) ? type : "UNKNOWN"}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
    >
      {hasWorkTypeIcon(type) ? SHAPES[type] : UNKNOWN}
    </svg>
  );
}
